/**
 * @fileoverview Sudoku Settings Modal Component
 * @description Admin modal for configuring Sudoku game settings
 */

import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  TextInput,
  TouchableOpacity,
  Switch,
  ScrollView,
  ActivityIndicator,
  useColorScheme,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  SudokuDifficulty,
  DIFFICULTY_CONFIGS,
  SudokuWeekendConfig,
  DEFAULT_WEEKEND_CONFIG,
} from "../types";

interface SudokuSettingsModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (config: SudokuWeekendConfig) => Promise<void>;
  initialConfig?: SudokuWeekendConfig;
  isSaving?: boolean;
}

/**
 * Modal for configuring Sudoku weekend settings
 */
export const SudokuSettingsModal: React.FC<SudokuSettingsModalProps> = ({
  visible,
  onClose,
  onSave,
  initialConfig = DEFAULT_WEEKEND_CONFIG,
  isSaving = false,
}) => {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const [config, setConfig] = useState<SudokuWeekendConfig>(initialConfig);

  // Reset config when modal opens
  useEffect(() => {
    if (visible) {
      setConfig(initialConfig);
    }
  }, [visible, initialConfig]);

  const colors = {
    background: isDark ? "#1F2937" : "#FFFFFF",
    surface: isDark ? "#111827" : "#F9FAFB",
    text: isDark ? "#F9FAFB" : "#111827",
    textSecondary: isDark ? "#9CA3AF" : "#6B7280",
    border: isDark ? "#374151" : "#E5E7EB",
    primary: "#3B82F6",
  };

  /**
   * Update points for a difficulty
   */
  const handlePointsChange = (difficulty: SudokuDifficulty, value: string) => {
    const numValue = parseInt(value, 10) || 0;
    setConfig((prev) => ({
      ...prev,
      points_per_difficulty: {
        ...prev.points_per_difficulty,
        [difficulty]: numValue,
      },
    }));
  };

  /**
   * Increment/decrement points
   */
  const adjustPoints = (difficulty: SudokuDifficulty, delta: number) => {
    setConfig((prev) => ({
      ...prev,
      points_per_difficulty: {
        ...prev.points_per_difficulty,
        [difficulty]: Math.max(
          0,
          prev.points_per_difficulty[difficulty] + delta
        ),
      },
    }));
  };

  /**
   * Handle save
   */
  const handleSave = async () => {
    await onSave(config);
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          padding: 24,
        }}
      >
        <View
          style={{
            width: "100%",
            maxWidth: 400,
            borderRadius: 24,
            backgroundColor: colors.background,
            padding: 24,
            maxHeight: "90%",
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  backgroundColor: "#8B5CF620",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Ionicons name="grid-outline" size={22} color="#8B5CF6" />
              </View>
              <Text
                style={{
                  fontSize: 20,
                  fontWeight: "700",
                  color: colors.text,
                }}
              >
                Sudoku Instellingen
              </Text>
            </View>
            <TouchableOpacity onPress={onClose}>
              <Ionicons name="close" size={24} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Points per difficulty */}
            <Text
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: colors.textSecondary,
                marginBottom: 12,
              }}
            >
              PUNTEN PER MOEILIJKHEID
            </Text>

            {(["easy", "medium", "hard"] as SudokuDifficulty[]).map(
              (difficulty) => {
                const diffConfig = DIFFICULTY_CONFIGS[difficulty];
                return (
                  <View
                    key={difficulty}
                    style={{
                      backgroundColor: colors.surface,
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 10,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                      }}
                    >
                      <View
                        style={{ flexDirection: "row", alignItems: "center", gap: 8 }}
                      >
                        <View
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: diffConfig.color,
                          }}
                        />
                        <Text
                          style={{
                            fontSize: 16,
                            fontWeight: "600",
                            color: colors.text,
                          }}
                        >
                          {diffConfig.label}
                        </Text>
                      </View>
                      <Text
                        style={{
                          fontSize: 12,
                          color: colors.textSecondary,
                        }}
                      >
                        {diffConfig.description}
                      </Text>
                    </View>

                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 12,
                      }}
                    >
                      <TouchableOpacity
                        onPress={() => adjustPoints(difficulty, -5)}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          backgroundColor: colors.background,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Ionicons name="remove" size={22} color={colors.text} />
                      </TouchableOpacity>

                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          backgroundColor: colors.background,
                          borderRadius: 12,
                          paddingHorizontal: 16,
                          paddingVertical: 8,
                        }}
                      >
                        <TextInput
                          value={config.points_per_difficulty[difficulty].toString()}
                          onChangeText={(text) =>
                            handlePointsChange(difficulty, text)
                          }
                          keyboardType="number-pad"
                          style={{
                            fontSize: 24,
                            fontWeight: "700",
                            color: diffConfig.color,
                            textAlign: "center",
                            minWidth: 60,
                          }}
                        />
                        <Text
                          style={{
                            fontSize: 14,
                            color: colors.textSecondary,
                            marginLeft: 4,
                          }}
                        >
                          pt
                        </Text>
                      </View>

                      <TouchableOpacity
                        onPress={() => adjustPoints(difficulty, 5)}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          backgroundColor: colors.background,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Ionicons name="add" size={22} color={colors.text} />
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              }
            )}

            {/* Perfect bonus */}
            <Text
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: colors.textSecondary,
                marginTop: 16,
                marginBottom: 12,
              }}
            >
              BONUSPUNTEN
            </Text>

            <View
              style={{
                backgroundColor: colors.surface,
                borderRadius: 12,
                padding: 12,
                marginBottom: 16,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <Ionicons name="star" size={18} color="#F59E0B" />
                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: "600",
                      color: colors.text,
                    }}
                  >
                    Perfecte Score Bonus
                  </Text>
                </View>
              </View>
              <Text
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  marginBottom: 12,
                }}
              >
                Extra punten voor voltooien zonder fouten en hints
              </Text>

              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                }}
              >
                <TouchableOpacity
                  onPress={() =>
                    setConfig((prev) => ({
                      ...prev,
                      perfect_bonus: Math.max(0, prev.perfect_bonus - 5),
                    }))
                  }
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    backgroundColor: colors.background,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Ionicons name="remove" size={22} color={colors.text} />
                </TouchableOpacity>

                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor: colors.background,
                    borderRadius: 12,
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                  }}
                >
                  <TextInput
                    value={config.perfect_bonus.toString()}
                    onChangeText={(text) =>
                      setConfig((prev) => ({
                        ...prev,
                        perfect_bonus: parseInt(text, 10) || 0,
                      }))
                    }
                    keyboardType="number-pad"
                    style={{
                      fontSize: 24,
                      fontWeight: "700",
                      color: "#F59E0B",
                      textAlign: "center",
                      minWidth: 60,
                    }}
                  />
                  <Text
                    style={{
                      fontSize: 14,
                      color: colors.textSecondary,
                      marginLeft: 4,
                    }}
                  >
                    pt
                  </Text>
                </View>

                <TouchableOpacity
                  onPress={() =>
                    setConfig((prev) => ({
                      ...prev,
                      perfect_bonus: prev.perfect_bonus + 5,
                    }))
                  }
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    backgroundColor: colors.background,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Ionicons name="add" size={22} color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Play limits */}
            <Text
              style={{
                fontSize: 14,
                fontWeight: "600",
                color: colors.textSecondary,
                marginBottom: 12,
              }}
            >
              SPEELLIMIET
            </Text>

            <View
              style={{
                backgroundColor: colors.surface,
                borderRadius: 12,
                padding: 12,
                marginBottom: 16,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: 16,
                      fontWeight: "600",
                      color: colors.text,
                    }}
                  >
                    Onbeperkt spelen
                  </Text>
                  <Text
                    style={{
                      fontSize: 12,
                      color: colors.textSecondary,
                    }}
                  >
                    Geen limiet op aantal partijen per dag
                  </Text>
                </View>
                <Switch
                  value={config.allow_unlimited_plays}
                  onValueChange={(value) =>
                    setConfig((prev) => ({
                      ...prev,
                      allow_unlimited_plays: value,
                    }))
                  }
                  trackColor={{ false: colors.border, true: colors.primary }}
                  thumbColor="#FFFFFF"
                />
              </View>

              {!config.allow_unlimited_plays && (
                <View>
                  <Text
                    style={{
                      fontSize: 14,
                      color: colors.text,
                      marginBottom: 8,
                    }}
                  >
                    Maximaal aantal partijen per dag:
                  </Text>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <TouchableOpacity
                      onPress={() =>
                        setConfig((prev) => ({
                          ...prev,
                          max_plays_per_day: Math.max(1, prev.max_plays_per_day - 1),
                        }))
                      }
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: colors.background,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Ionicons name="remove" size={20} color={colors.text} />
                    </TouchableOpacity>

                    <View
                      style={{
                        backgroundColor: colors.background,
                        borderRadius: 10,
                        paddingHorizontal: 16,
                        paddingVertical: 8,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 20,
                          fontWeight: "700",
                          color: colors.primary,
                        }}
                      >
                        {config.max_plays_per_day}
                      </Text>
                    </View>

                    <TouchableOpacity
                      onPress={() =>
                        setConfig((prev) => ({
                          ...prev,
                          max_plays_per_day: prev.max_plays_per_day + 1,
                        }))
                      }
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: colors.background,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Ionicons name="add" size={20} color={colors.text} />
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>

            {/* Info card */}
            <View
              style={{
                backgroundColor: "#3B82F615",
                borderRadius: 12,
                padding: 12,
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <Ionicons name="information-circle" size={20} color="#3B82F6" />
              <Text
                style={{
                  flex: 1,
                  fontSize: 13,
                  color: colors.textSecondary,
                  lineHeight: 18,
                }}
              >
                Sudoku is een solo spel. Spelers verdienen punten voor hun team
                bij het voltooien van een puzzel. Hints tellen mee tegen een
                "perfecte" score.
              </Text>
            </View>
          </ScrollView>

          {/* Action buttons */}
          <View
            style={{
              flexDirection: "row",
              gap: 12,
              marginTop: 20,
            }}
          >
            <TouchableOpacity
              onPress={onClose}
              style={{
                flex: 1,
                paddingVertical: 14,
                borderRadius: 12,
                backgroundColor: colors.surface,
                alignItems: "center",
              }}
            >
              <Text
                style={{
                  fontSize: 16,
                  fontWeight: "600",
                  color: colors.text,
                }}
              >
                Annuleren
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handleSave}
              disabled={isSaving}
              style={{
                flex: 1,
                paddingVertical: 14,
                borderRadius: 12,
                backgroundColor: colors.primary,
                alignItems: "center",
                opacity: isSaving ? 0.7 : 1,
              }}
            >
              {isSaving ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: "600",
                    color: "#FFFFFF",
                  }}
                >
                  Opslaan
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

